import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import type { Faro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import type { App } from 'vue';

let faro: Faro | null = null;

function readEnv(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim();

  return trimmed ? trimmed : fallback;
}

/**
 * Origens para as quais o traceparent/tracestate deve ser propagado nas chamadas ao backend.
 *
 * Em produção on-premise o VITE_API_BASE_URL é relativo (ex.: "/api"): front e API são
 * same-origin, então a instrumentação já injeta o trace context sem precisar listar CORS urls
 * (e o CORS do backend nem é acionado). Só quando a base é uma URL absoluta (dev/staging, ex.:
 * "http://localhost:3000/api") a chamada é cross-origin e a origem precisa ser propagada — aí o
 * backend também aceita os headers traceparent/tracestate no CORS.
 */
function resolveTracePropagationTargets(): RegExp[] {
  const apiBase = import.meta.env.VITE_API_BASE_URL?.trim();

  if (!apiBase) {
    return [];
  }

  try {
    const { origin } = new URL(apiBase);
    const escaped = origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // (?:/|$) ancora no fim da origem: só casa a origem exata ou seguida de um path, evitando que
    // domínios sósia (api.example.com.evil) ou portas diferentes (api.example.com:444) recebam o
    // traceparent por match de prefixo.
    return [new RegExp(`^${escaped}(?:/|$)`)];
  } catch {
    return [];
  }
}

/**
 * Inicializa o Grafana Faro o mais cedo possível no bootstrap, antes do createApp, para que
 * a auto-instrumentação já capture erros não tratados (window.onerror / unhandledrejection) e
 * os Web Vitals (LCP/INP/CLS) desde o primeiro instante da sessão.
 *
 * É um no-op quando VITE_FARO_URL não está configurada (dev/local): sem endpoint de coleta, nada
 * é enviado. A configuração é build-time (Vite), então essas variáveis chegam pelo --build-arg da
 * imagem de produção. Memoiza a instância — initializeFaro não pode ser chamado duas vezes na mesma
 * página.
 */
export function initFaro(): Faro | null {
  if (faro) {
    return faro;
  }

  const url = import.meta.env.VITE_FARO_URL?.trim();

  if (!url) {
    return null;
  }

  faro = initializeFaro({
    url,
    app: {
      name: readEnv(import.meta.env.VITE_FARO_APP_NAME, 'gd-frontend'),
      version: readEnv(import.meta.env.VITE_FARO_APP_VERSION, 'unknown'),
      environment: readEnv(import.meta.env.VITE_FARO_ENV, 'unknown'),
    },
    instrumentations: [
      ...getWebInstrumentations({ captureConsole: false }),
      new TracingInstrumentation({
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: resolveTracePropagationTargets(),
        },
      }),
    ],
    sessionTracking: {
      enabled: true,
    },
  });

  return faro;
}

/**
 * Encaminha os erros capturados pelo Vue (app.config.errorHandler) ao Faro, complementando a
 * auto-instrumentação global. Sem essa ponte, erros lançados dentro do ciclo de vida dos
 * componentes seriam engolidos pelo Vue e não chegariam à observabilidade. No-op quando o Faro
 * não foi inicializado (URL ausente).
 */
export function setupFaro(app: App): void {
  if (!faro) {
    return;
  }

  const faroInstance = faro;
  const previousHandler = app.config.errorHandler;

  app.config.errorHandler = (error, instance, info) => {
    faroInstance.api.pushError(error instanceof Error ? error : new Error(String(error)), {
      context: { source: 'vue-error-handler', info },
    });

    // Preserva um handler já registrado; sem ele, ecoa no console (paridade com o comportamento
    // padrão do Vue, que aqui deixaria de logar por termos definido o errorHandler — a captura de
    // console do Faro está desligada, então não há realimentação).
    if (previousHandler) {
      previousHandler(error, instance, info);
    } else {
      console.error(error);
    }
  };
}
